import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { consultantsService, ConsultantService, CreateConsultantServiceDto } from '../../services/consultants.service';
import fieldsService, { Field } from '../../services/fields.service';
import subcategoriesService, { Subcategory } from '../../services/subcategories.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { API_ENDPOINTS } from '../../config/api';
import { 
  Briefcase, 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar,
  Plus,
  Clock,
  XCircle,
  Trash2,
  Edit3
} from 'lucide-react';

const EditServicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [service, setService] = useState<ConsultantService | null>(null);
  const [formData, setFormData] = useState<CreateConsultantServiceDto>({
    title: '',
    reference: '',
    country: '',
    city: '',
    wilaya_id: undefined,
    domaine_id: 0,
    subcategory_id: '',
    format: 'presentiel',
    language: '',
    estimated_duration: '',
    full_description: '',
    skills_gained: '',
    learning_objectives: '',
    target_audience: '',
    consultant_names: '',
    banner_image: undefined,
    detailed_program_pdf: undefined,
  });
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [durationValue, setDurationValue] = useState<number>(0);
  const [durationUnit, setDurationUnit] = useState<string>('heures');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Data states
  const [fields, setFields] = useState<Field[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  
  // Loading states
  const [loadingFields, setLoadingFields] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCreneaux, setLoadingCreneaux] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  // Load all necessary data
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load all data in parallel
      const [serviceData, fieldsData, subcategoriesData, countriesData, creneauxData] = await Promise.all([
        consultantsService.getService(id!),
        fieldsService.getFields(),
        subcategoriesService.getSubcategories(),
        countriesService.getCountries(),
        creneauxService.getCreneauxByService(id!)
      ]);
      
      setService(serviceData);
      setFields(fieldsData);
      setSubcategories(subcategoriesData);
      setCountries(countriesData);
      setCreneaux(creneauxData);
      
      // Parse duration if it exists - backend returns 'duration' field, not 'estimated_duration'
      let durationValue = 0;
      let durationUnit = 'heures';
      const durationString = serviceData.duration || serviceData.estimated_duration;
      if (durationString) {
        const durationMatch = durationString.match(/(\d+)\s*(\w+)/);
        if (durationMatch) {
          durationValue = parseInt(durationMatch[1]);
          durationUnit = durationMatch[2];
        }
      }

      // Set form data
      const countryForForm = typeof serviceData.country === 'string' ? serviceData.country : serviceData.country?.name || '';
      const wilayaIdForForm = serviceData.wilaya_id || serviceData.training_center?.wilaya_id || undefined;
      
      
      setFormData({
        title: serviceData.title || '',
        reference: serviceData.reference || '',
        country: countryForForm,
        city: serviceData.city || '',
        wilaya_id: wilayaIdForForm,
        domaine_id: serviceData.domaine_id || 0,
        subcategory_id: serviceData.subcategory_id || '',
        format: serviceData.format as 'presentiel' | 'distance' | 'hybride' || 'presentiel',
        language: serviceData.language || '',
        estimated_duration: serviceData.duration || serviceData.estimated_duration || '',
        full_description: serviceData.full_description || '',
        skills_gained: serviceData.skills_gained || '',
        learning_objectives: serviceData.learning_objectives || '',
        target_audience: serviceData.target_audience || '',
        consultant_names: serviceData.consultant_names || '',
        banner_image: serviceData.banner_image || undefined,
        detailed_program_pdf: serviceData.detailed_program_pdf || undefined,
      });

      // Set duration values
      setDurationValue(durationValue);
      setDurationUnit(durationUnit);
      
     
      
      // Load wilayas if country is set
      const countryName = typeof serviceData.country === 'string' ? serviceData.country : serviceData.country?.name;
      const trainingCenterCountryName = serviceData.training_center?.country?.name;
      
      
      if (countryName || trainingCenterCountryName) {
        // Try to find country by service country first, then training center country
        const countryToUse = countryName || trainingCenterCountryName;
        const country = countriesData.find(c => c.name === countryToUse);
        
        
        if (country) {
          const wilayasData = await countriesService.getWilayas(country.id);
          setWilayas(wilayasData);
        } else {
          // Country not found
        }
      }
      
    } catch (err) {
      setError(t('consultant.edit.errors.load', 'Error loading service'));
    } finally {
      setIsLoading(false);
      setLoadingFields(false);
      setLoadingSubcategories(false);
      setLoadingCountries(false);
      setLoadingCreneaux(false);
    }
  };

  // Load wilayas when country changes
  useEffect(() => {
    if (formData.country) {
      const loadWilayas = async () => {
        try {
          // Find country ID by name
          const country = countries.find(c => c.name === formData.country);
          
          if (country) {
            const wilayasData = await countriesService.getWilayas(country.id);
            setWilayas(wilayasData);
          } else {
            // Country not found for wilayas
          }
        } catch (error) {
          // Error loading wilayas
        }
      };
      loadWilayas();
    } else {
      setWilayas([]);
    }
  }, [formData.country, countries]);

  // Debug effect to see current form values
  useEffect(() => {
    // Debug effect removed - no longer needed
  }, [formData, durationValue, durationUnit]);

  const handleInputChange = (field: keyof CreateConsultantServiceDto, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const handleDurationChange = (value: number, unit: string) => {
    setDurationValue(value);
    setDurationUnit(unit);
    const durationString = value > 0 ? `${value} ${unit}` : '';
    setFormData(prev => ({
      ...prev,
      estimated_duration: durationString
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCreneauStatus = (creneau: Creneau) => {
    if (creneau.is_booked) return { text: t('consultant.edit.creneaux.status.booked', 'Booked'), color: 'text-red-600', icon: XCircle };
    if (creneau.is_available) return { text: t('consultant.edit.creneaux.status.available', 'Available'), color: 'text-green-600', icon: CheckCircle };
    return { text: t('consultant.edit.creneaux.status.unavailable', 'Unavailable'), color: 'text-gray-600', icon: AlertCircle };
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFile(file);
      setFormData(prev => ({ ...prev, banner_image: URL.createObjectURL(file) }));
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPdfFile(file);
      setFormData(prev => ({ ...prev, detailed_program_pdf: URL.createObjectURL(file) }));
    }
  };

  const removeBanner = () => {
    setBannerFile(null);
    setFormData(prev => ({ ...prev, banner_image: undefined }));
  };

  const removePdf = () => {
    setPdfFile(null);
    setFormData(prev => ({ ...prev, detailed_program_pdf: undefined }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError('Le titre de la prestation est requis');
      return false;
    }
    if (!formData.reference?.trim()) {
      setError('La référence est requise');
      return false;
    }
    if (!formData.country) {
      setError('Le pays est requis');
      return false;
    }
    if (!formData.wilaya_id) {
      setError('La wilaya/ville est requise');
      return false;
    }
    if (!formData.domaine_id) {
      setError('La classification/catégorie d\'expertise est requise');
      return false;
    }
    if (!formData.full_description.trim()) {
      setError('La description complète est requise');
      return false;
    }
    if (!formData.skills_gained?.trim()) {
      setError('Les compétences mises en œuvre sont requises');
      return false;
    }
    if (!formData.learning_objectives?.trim()) {
      setError('Les objectifs de la prestation sont requis');
      return false;
    }
    if (!formData.target_audience?.trim()) {
      setError('Les entreprises/publics cibles sont requis');
      return false;
    }
    if (!formData.consultant_names?.trim()) {
      setError('Le(s) nom(s) des consultants intervenants est requis');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSaving(true);
    setError(null);

    try {
      // Update the service
      await consultantsService.updateService(id!, formData);
      setSuccessMessage(t('consultant.edit.success.updated', 'Service updated successfully!'));
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/consultant/services');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('consultant.edit.errors.update', 'Error updating service'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('consultant.edit.notFound.title', 'Service not found')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('consultant.edit.notFound.subtitle', 'The requested service does not exist.')}</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/consultant/services')}
            className="inline-flex items-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            {t('consultant.common.backToServices', 'Back to services')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/consultant/services')}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('consultant.edit.header.title', 'Edit service')}</h1>
            <p className="text-gray-600">{service.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/consultant/services/${id}/creneaux`)}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-secondary to-pink-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-brand-secondary transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {t('consultant.edit.header.manageSlots', 'Manage slots')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('consultant.edit.actions.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('consultant.edit.actions.save', 'Save')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800 font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('consultant.edit.form.general', 'General information')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Titre de la prestation */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.title', 'Service title *')}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.titlePlaceholder', 'Ex: Pharmaceutical Audit and Regulatory Compliance')}
              />
            </div>

            {/* Référence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.reference', 'Reference *')}
              </label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.referencePlaceholder', 'Ex: AUDIT-PHARMA-2024')}
              />
            </div>

            {/* Pays */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.country', 'Country *')}
              </label>
              <select
                value={formData.country || ''}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              >
                <option value="">{t('consultant.edit.form.selectCountry', 'Select a country')}</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Wilaya / Ville */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.wilayaCity', 'Wilaya / City *')}
              </label>
              <select
                value={formData.wilaya_id || ''}
                onChange={(e) => {
                  handleInputChange('wilaya_id', e.target.value ? parseInt(e.target.value) : undefined);
                }}
                disabled={!formData.country}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('consultant.edit.form.selectWilaya', 'Select a wilaya/city')}</option>
                {wilayas.map((wilaya) => (
                  <option key={wilaya.id} value={wilaya.id}>
                    {wilaya.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Classification / Catégorie d'expertise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Classification / Catégorie d'expertise *
              </label>
              <select
                value={formData.domaine_id || ''}
                onChange={(e) => handleInputChange('domaine_id', e.target.value ? parseInt(e.target.value) : 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              >
                <option value="">Sélectionnez une catégorie</option>
                {fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name_french}
                  </option>
                ))}
              </select>
            </div>

            {/* Format de prestation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Format de prestation *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'presentiel', label: 'Présentiel' },
                  { value: 'distance', label: 'À distance' },
                  { value: 'hybride', label: 'Hybride' }
                ].map((format) => (
                  <label key={format.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="format"
                      value={format.value}
                      checked={formData.format === format.value}
                      onChange={(e) => handleInputChange('format', e.target.value as 'presentiel' | 'distance' | 'hybride')}
                      className="text-brand-primary focus:ring-brand-primary"
                    />
                    <span className="text-sm text-gray-700">{format.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Langue(s) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue(s)
              </label>
              <select
                value={formData.language}
                onChange={(e) => {
                  handleInputChange('language', e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              >
                <option value="">Sélectionner la langue</option>
                <option value="Arabe">Arabe</option>
                <option value="Anglais">Anglais</option>
                <option value="Français">Français</option>
                <option value="Arabe/Anglais">Arabe/Anglais</option>
              </select>
            </div>

            {/* Durée estimée */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée estimée
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={durationValue}
                  onChange={(e) => {
                    handleDurationChange(Number(e.target.value), durationUnit);
                  }}
                  placeholder="0"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
                <select
                  value={durationUnit}
                  onChange={(e) => handleDurationChange(durationValue, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                >
                  <option value="heures">Heures</option>
                  <option value="jours">Jours</option>
                  <option value="semaines">Semaines</option>
                  <option value="mois">Mois</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Description and Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('consultant.edit.form.descriptionContent', 'Description and content')}</h2>
          
          <div className="space-y-6">
            {/* Description complète */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.fullDescription', 'Full description *')}
              </label>
              <textarea
                value={formData.full_description}
                onChange={(e) => handleInputChange('full_description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.fullDescriptionPlaceholder', 'Describe your service, methodology, deliverables...')}
              />
            </div>

            {/* Compétences mises en œuvre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.skills', 'Skills applied / Areas covered *')}
              </label>
              <textarea
                value={formData.skills_gained}
                onChange={(e) => handleInputChange('skills_gained', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.skillsPlaceholder', 'List your technical skills, expertise areas, certifications...')}
              />
            </div>

            {/* Objectifs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.objectives', 'Service objectives *')}
              </label>
              <textarea
                value={formData.learning_objectives}
                onChange={(e) => handleInputChange('learning_objectives', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.objectivesPlaceholder', 'Define the specific objectives for this service...')}
              />
            </div>

            {/* Publics cibles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.target', 'Target companies / Audience *')}
              </label>
              <textarea
                value={formData.target_audience}
                onChange={(e) => handleInputChange('target_audience', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.targetPlaceholder', 'Describe company types, sectors, sizes you target...')}
              />
            </div>

            {/* Consultants intervenants */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.form.consultants', 'Consultants names *')}
              </label>
              <input
                type="text"
                value={formData.consultant_names}
                onChange={(e) => handleInputChange('consultant_names', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                placeholder={t('consultant.edit.form.consultantsPlaceholder', 'Ex: Dr. Ahmed BENALI, Pharm. Sarah KADRI')}
              />
            </div>

          </div>
        </div>

        {/* Files */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('consultant.edit.files.title', 'Files')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bannière / Image de couverture */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.files.banner', 'Banner / Cover image')}
              </label>
              <div className="space-y-2">
                {(formData.banner_image || bannerFile) && (
                  <div className="relative">
                    <img
                      src={bannerFile ? URL.createObjectURL(bannerFile) : formData.banner_image?.startsWith('http') ? formData.banner_image : `${API_ENDPOINTS.UPLOADS}/${formData.banner_image}`}
                      alt="Banner preview"
                      className="h-20 w-32 rounded-lg object-cover border border-gray-300"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={removeBanner}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-primary file:text-white hover:file:bg-brand-primary/90"
                />
                <p className="text-xs text-gray-500">{t('consultant.edit.files.bannerNote', 'PNG, JPG up to 2MB')}</p>
              </div>
            </div>

            {/* Document PDF */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('consultant.edit.files.pdf', 'Presentation PDF (optional)')}
              </label>
              <div className="space-y-2">
                {(formData.detailed_program_pdf || pdfFile) && (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Briefcase className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {pdfFile ? pdfFile.name : t('consultant.edit.files.existingPdf', 'Existing PDF')}
                    </span>
                    <button
                      type="button"
                      onClick={removePdf}
                      className="ml-auto text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
                <p className="text-xs text-gray-500">{t('consultant.edit.files.pdfNote', 'PDF up to 5MB')}</p>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Créneaux Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-brand-primary" />
              Créneaux disponibles
            </h2>
            <p className="text-gray-600 mt-1">
              Gérez les créneaux de rendez-vous pour cette prestation
            </p>
          </div>
          <button
            onClick={() => navigate(`/consultant/services/${id}/creneaux`)}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-secondary to-pink-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-brand-secondary transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <Plus className="h-4 w-4 mr-2" />
            Gérer les créneaux
          </button>
        </div>

        {loadingCreneaux ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
            <span className="ml-2 text-gray-600">Chargement des créneaux...</span>
          </div>
        ) : creneaux.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun créneau créé</h3>
            <p className="text-gray-600 mb-4">
              Créez votre premier créneau pour permettre aux clients de réserver des rendez-vous.
            </p>
            <button
              onClick={() => navigate(`/consultant/services/${id}/creneaux`)}
              className="inline-flex items-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer un créneau
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4">
              {creneaux.map((creneau) => {
                const status = getCreneauStatus(creneau);
                const StatusIcon = status.icon;
                
                return (
                  <div key={creneau.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`h-5 w-5 ${status.color}`} />
                        <span className={`text-sm font-medium ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {formatDate(creneau.creneau_date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">
                            {creneau.start_time} - {creneau.end_time}
                          </span>
                        </div>
                      </div>
                      
                      {creneau.is_booked && creneau.trainee && (
                        <div className="text-sm text-gray-600">
                          Réservé par: {creneau.trainee.firstname} {creneau.trainee.lastname}
                          {creneau.trainee.email && ` (${creneau.trainee.email})`}
                        </div>
                      )}
                      
                      {creneau.notes && (
                        <div className="text-sm text-gray-600">
                          Notes: {creneau.notes}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {!creneau.is_booked && (
                        <>
                          <button
                            onClick={() => navigate(`/consultant/services/${id}/creneaux`)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Modifier le créneau"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate(`/consultant/services/${id}/creneaux`)}
                className="inline-flex items-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-primary/90 transition-colors"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Voir tous les créneaux ({creneaux.length})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditServicePage;
